import { useMemo } from 'react';
import { DEMO_FARM, type Farm } from '@/hooks/useMyFarms';
import type { FarmStatus } from '@/lib/farm/status';
import type { ProducerFormData } from '@/features/producer-editor';

interface Producer {
  id: string;
  name: string;
  short_description: string | null;
  description: string | null;
  address_text: string | null;
  city: string | null;
  region: string | null;
  county: string | null;
  lat: number | null;
  lng: number | null;
  categories: string[];
  phone: string | null;
  website: string | null;
  instagram: string | null;
  facebook: string | null;
  email: string | null;
  opening_hours_text: string | null;
  phone_public?: boolean;
  email_public?: boolean;
  website_public?: boolean;
  instagram_public?: boolean;
  facebook_public?: boolean;
  has_farm_shop?: boolean;
  has_farm_sale_alcohol?: boolean;
  capability_suggestion?: string | null;
  farm_shop_intro?: string | null;
  farm_shop_opening_hours_text?: string | null;
  farm_shop_status?: string | null;
  slug: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

export function useProducerFormData(
  producer: Producer | null | undefined,
  isDemo: boolean,
  farmId: string | undefined
) {
  const initialFormData = useMemo((): Partial<ProducerFormData> | undefined => {
    if (isDemo) {
      return {
        name: DEMO_FARM.name,
        shortDescription: DEMO_FARM.descriptionShort || '',
        longDescription: '',
        status: 'draft',
      };
    }
    if (producer) {
      return {
        name: producer.name,
        slug: producer.slug || '',
        shortDescription: producer.short_description || '',
        longDescription: producer.description || '',
        address: producer.address_text || '',
        city: producer.city || '',
        region: producer.region || producer.county || '',
        lat: producer.lat != null ? String(producer.lat) : '',
        lng: producer.lng != null ? String(producer.lng) : '',
        categories: producer.categories || [],
        phone: producer.phone || '',
        website: producer.website || '',
        instagram: producer.instagram || '',
        facebook: producer.facebook || '',
        email: producer.email || '',
        openingHoursText: producer.opening_hours_text || '',
        farmShopIntro: producer.farm_shop_intro || '',
        farmShopOpeningHoursText: producer.farm_shop_opening_hours_text || '',
        phonePublic: producer.phone_public ?? false,
        emailPublic: producer.email_public ?? false,
        websitePublic: producer.website_public ?? false,
        instagramPublic: producer.instagram_public ?? false,
        facebookPublic: producer.facebook_public ?? false,
        hasFarmShop: producer.has_farm_shop ?? false,
        hasFarmSaleAlcohol: producer.has_farm_sale_alcohol ?? false,
        farmShopStatus: (producer.farm_shop_status as 'open' | 'closed' | 'seasonal') || 'closed',
        capabilitySuggestion: producer.capability_suggestion || '',
        coverUrl: '',
        galleryUrls: [''],
        coverAssets: [],
        galleryAssets: [],
        foodDescription: (producer as any).food_description || '',
        foodServingTypes: (producer as any).food_serving_types || [],
        status: producer.status as ProducerFormData['status'],
        verified: false,
      };
    }
    return undefined;
  }, [producer, isDemo]);

  const mapFormStatusToFarmStatus = (status: ProducerFormData['status']): FarmStatus => {
    if (status === 'public') return 'public';
    return 'draft';
  };

  const farmForChecklist = (formData: ProducerFormData): Farm => ({
    id: farmId || 'demo',
    name: formData.name,
    status: mapFormStatusToFarmStatus(formData.status),
    descriptionShort: formData.shortDescription,
    city: formData.city,
    county: formData.region,
    createdAt: producer?.created_at || new Date().toISOString(),
  });

  return {
    initialFormData,
    mapFormStatusToFarmStatus,
    farmForChecklist,
  };
}
